<template>
    <div v-if="renderComponent" class="relative">
        <div class="p-4 my-3 border border-blue-200 rounded-md bg-blue-50" v-if="orderInfo">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg
                        class="w-5 h-5 text-blue-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="flex-1 ml-3 md:flex md:justify-between">
                    <p class="text-sm text-blue-700">
                        U kunt door het verslepen van de de pagina's de volgorde wijzigen. Pagina's zijn ook onder
                        andere pagina's te plaatsen, door bij het verplaatsen de pagina een tikje naar rechts te houden
                        en de muisknop los te laten. Klik op "opslaan" wanneer de volgorde naar wens is.
                    </p>
                </div>
            </div>
        </div>

        <div class="p-4 my-3 border border-green-200 rounded-md bg-green-50" v-if="orderSaved">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg
                        class="w-5 h-5 text-green-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="flex-1 ml-3 md:flex md:justify-between">
                    <p class="text-sm text-green-700">Volgorde is succesvol opgeslagen</p>
                </div>
            </div>
        </div>

        <div
            class="website::admin-websitePages-index-TreeViewComponent"
            :class="{ drag: !!draggableEnabled }"
            dusk="websitePageTree"
        >
            <table class="dataTable">
                <thead>
                <tr role="row">
                    <th>
                        <span class="hidden lg:block">Pagina titel</span>
                        <span class="lg:hidden">Pagina's'</span>
                    </th>
                    <th class="hidden lg:table-cell" width="60">Type</th>
                    <th class="hidden statistics lg:table-cell" width="70">Statistieken</th>
                    <th class="hidden language lg:table-cell" v-for="language in languages" v-if="languages.length > 1" :key="language">
                        <i :class="'fi fi-' + (language === 'en' ? 'gb' : language)"></i>
                    </th>
                    <th class="hidden user lg:table-cell" width="130">Laatst gewijzigd</th>
                    <th class="hidden lg:table-cell" width="100">Gepubliceerd</th>
                    <th class="hidden flex-nowrap lg:table-cell" width="130">Acties</th>
                </tr>
                </thead>
            </table>

            <div v-if="localWebsiteMenuPages.length < 1" class="px-6 py-4 empty-tree">
                <span>Geen items gevonden</span>
            </div>

            <Tree
                v-else
                ref="Tree"
                :value="localWebsiteMenuPages"
                :draggable="draggableEnabled"
                :indent="30"
                @change="fixTreeBackgroundColors()"
                @drag="startChildIndicator($event)"
                @drop="stopChildIndicator()"
            >
                <template v-slot="{ node, path, store }">
                    <div :dusk="'row-' + (node.website_page ? node.website_page.id : '')"
                        :class="{
                            'languages': languages.length > 1,
                        }"
                    >
                        <template v-if="!node.isDragPlaceHolder">
                            <span
                                :class="draggableEnabled ? 'select-none' : ''"
                                class="page-title-cell inline-flex items-center py-2 lg:py-4 pr-3 text-lg font-medium text-gray-900 truncate whitespace-nowrap lg:text-sm"
                                :title="node.website_page
                                    ? (node.website_page.title_menu || {})[getLanguage] ||
                                      (node.website_page.title_page || {})[getLanguage]
                                    : '?'"
                            >
                                <b
                                    v-if="getNodeLevel(path) !== 1"
                                    :dusk="'level-' + getNodeLevel(path)"
                                    :title="'Niveau ' + getNodeLevel(path)"
                                    class="inline-flex items-center flex-shrink-0 mr-3"
                                >
                                    <svg
                                        class="w-6 h-6 transform rotate-180"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                                        ></path>
                                    </svg>

                                    <!-- Depth indicator for sub-sub items and deeper (level 3+). -->
                                    <span
                                        v-if="getNodeLevel(path) > 2"
                                        class="ml-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold leading-none text-gray-500 lg:hidden"
                                    >{{ getNodeLevel(path) }}</span>
                                </b>

                              {{
                                    node.website_page
                                        ? (node.website_page.title_menu || {})[getLanguage] ||
                                        (node.website_page.title_page || {})[getLanguage]
                                        : '?'
                                }}

                              <!--
                                A11y warnings badge — count is maintained by
                                CanvasBlockA11yObserver on every block save.
                                Hidden when 0 / column missing (project hasn't
                                run the Galaxy upgrade yet). Click navigates
                                to the page edit screen where the banner +
                                detail list lives.
                              -->
                              <span
                                  v-if="a11yEnabled && node.website_page && (node.website_page.a11y_warnings_count > 0)"
                                  class="ml-2 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700"
                                  :title="
                                      node.website_page.a11y_warnings_count === 1
                                          ? '1 toegankelijkheidsfout op deze pagina'
                                          : node.website_page.a11y_warnings_count + ' toegankelijkheidsfouten op deze pagina'
                                  "
                              >
                                <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
                                {{ node.website_page.a11y_warnings_count }}
                              </span>
                            </span>

                            <div class="type" v-if="node.website_page">
                                <span class="block mb-1 text-xs font-semibold uppercase text-gray-500 lg:hidden">
                                  Type
                                </span>
                                <span
                                    v-if="node.website_page.extra_type"
                                    class="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium"
                                    :class="{
                                        'bg-indigo-100 text-indigo-700': node.website_page.extra_type === 'module',
                                        'bg-amber-100 text-amber-700': node.website_page.extra_type === 'redirect',
                                        'bg-purple-100 text-purple-700': node.website_page.extra_type === 'group',
                                        'bg-gray-100 text-gray-600': node.website_page.extra_type === 'canvas',
                                    }"
                                >
                                    {{ ({ module: 'Module', redirect: 'Redirect', group: 'Groep', canvas: 'Pagina' })[node.website_page.extra_type] || node.website_page.extra_type }}
                                </span>
                            </div>

                            <div class="statistics" v-if="node.website_page">
                                <span class="block mb-1 text-xs font-semibold uppercase text-gray-500 lg:hidden">
                                  Statistieken
                                </span>
                                <SparkLineComponent
                                    :values="node.website_page.extra_statistics"
                                    :maxValue="maxStatisticValue"
                                />
                            </div>

                            <div class="languages" v-if="languages.length > 1">

                              <span class="block mb-1 text-xs font-semibold uppercase text-gray-500 lg:hidden">
                                Talen
                              </span>

                                <div class="language" v-for="language in languages">
                                    <span
                                        v-if="node.website_page"
                                    >
                                        <i
                                            :title="
                                                Math.round(
                                                    node.website_page.extra_translationCoverage[language] * 100
                                                ) + '% vertaald'
                                            "
                                            :class="{
                                                translated: true,
                                                fa: true,
                                                [node.website_page.extra_translationCoverage[language] === 0
                                                    ? 'fa-plus disabled'
                                                    : 'fa-check']: true,
                                                incomplete: node.website_page.extra_translationCoverage[language] < 1,
                                            }"
                                        ></i>
                                    </span>
                                </div>
                            </div>

                            <span class="user" v-if="node.website_page">

                              <span class="block mb-1 text-xs font-semibold uppercase text-gray-500 lg:hidden">
                                Laatst gewijzigd
                              </span>

                                <div class="flex" dusk="last_edited_user">
                                    <div class="profile-img">
                                        <img
                                            class="rounded-full img-fluid"
                                            v-if="node.website_page.last_edited_user"
                                            :src="node.website_page.last_edited_user.extra_avatar_url"
                                            alt="User avatar"
                                        />
                                        <img
                                            class="rounded-full img-fluid dummy"
                                            v-else
                                            src="/vendor/galaxy/canvas/img/blocks/common/placeholder_person.jpg"
                                            alt="User avatar"
                                            width="30"
                                            height="30"
                                        />
                                    </div>

                                    <div class="edited">
                                        <div class="name" v-if="node.website_page.last_edited_user">
                                            {{ node.website_page.last_edited_user.name }}
                                        </div>
                                        <div class="name" v-else>Onbekend</div>

                                        <div class="date">
                                            {{ node.website_page.updated_at || '1970-01-01' | formatDate }}
                                        </div>
                                    </div>
                                </div>
                            </span>

                            <div class="publish">

                              <span class="block mb-1 text-xs font-semibold uppercase text-gray-500 lg:hidden">
                                Gepubliceerd
                              </span>

                                <span
                                    v-if="node.website_page"
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium"
                                    :class="
                                        node.website_page.extra_isPublished
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                    "
                                >
                                    <svg
                                        class="-ml-0.5 mr-1.5 h-2 w-2"
                                        :class="node.website_page.extra_isPublished ? 'text-green-400' : 'text-red-400'"
                                        fill="currentColor"
                                        viewBox="0 0 8 8"
                                    >
                                        <circle cx="4" cy="4" r="3"/>
                                    </svg>
                                    {{ node.website_page.extra_isPublished ? 'Online' : 'Offline' }}
                                </span>
                            </div>

                            <div class="flex edit_actions flex-warp gap-y-2" dusk="edit_actions">
                                <div class="relative inline-block mr-3 text-left pageAdd" x-data="{ isOpen: false }">
                                    <button
                                        type="button"
                                        x-on:click="isOpen = !isOpen"
                                        dusk="extra"
                                        class="transition bg-indigo-600 rounded-full hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500"
                                    >
                                        <svg
                                            class="w-6 h-6"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="13.631"
                                            height="13.631"
                                            viewBox="0 0 13.631 13.631"
                                        >
                                            <g
                                                id="Icon_feather-plus"
                                                data-name="Icon feather-plus"
                                                transform="translate(-6.5 -6.5)"
                                            >
                                                <path
                                                    id="Path_120"
                                                    data-name="Path 120"
                                                    d="M18,7.5V19.131"
                                                    transform="translate(-4.685)"
                                                    fill="none"
                                                    stroke="#fff"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                />
                                                <path
                                                    id="Path_121"
                                                    data-name="Path 121"
                                                    d="M7.5,18H19.131"
                                                    transform="translate(0 -4.685)"
                                                    fill="none"
                                                    stroke="#fff"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                />
                                            </g>
                                        </svg>
                                    </button>

                                    <div
                                        x-cloak
                                        x-show="isOpen"
                                        x-on:click.away="isOpen = false"
                                        x-transition:enter="transition ease-out duration-100 transform"
                                        x-transition:enter-start="opacity-0 scale-95"
                                        x-transition:enter-end="opacity-100 scale-100"
                                        x-transition:leave="transition ease-in duration-75 transform"
                                        x-transition:leave-start="opacity-100 scale-100"
                                        x-transition:leave-end="opacity-0 scale-95"
                                        dusk="dropdown"
                                        class="absolute right-0 z-50 w-56 mt-1 origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                    >
                                        <a
                                            :href="
                                                '/admin/modules/website/website-pages/create?after=' +
                                                (node.website_page ? node.website_page.id : '') +
                                                '&menu=' +
                                                filteredWebsiteMenu.id
                                            "
                                            class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100 hover:text-gray-900"
                                            dusk="after_page"
                                        >
                                            <div class="title">
                                                <i class="fa fa-arrow-down"></i>
                                                Na pagina toevoegen
                                            </div>
                                        </a>

                                        <a
                                            :href="
                                                '/admin/modules/website/website-pages/create?submenu=' +
                                                (node.website_page ? node.website_page.id : '') +
                                                '&menu=' +
                                                filteredWebsiteMenu.id
                                            "
                                            class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100 hover:text-gray-900"
                                            dusk="subpage"
                                            v-if="filteredWebsiteMenu.name.toLowerCase() != 'los'"
                                        >
                                            <div class="title">
                                                <i class="fas fa-arrow-alt-from-left"></i>
                                                Als subpagina toevoegen
                                            </div>
                                        </a>

                                        <form
                                            action="/admin/modules/website/website-pages/duplicate"
                                            method="post"
                                            x-data="{ buttonDisabled: false }"
                                            x-on:submit="buttonDisabled = true"
                                            v-if="node.website_page.is_deletable == 1 && node.website_page.extra_canDuplicate !== false"
                                        >
                                            <input
                                                name="page"
                                                :value="node.website_page ? node.website_page.id : ''"
                                                hidden
                                            />
                                            <button
                                                class="flex w-full px-4 py-2 text-sm text-gray-700 transition page-dup hover:bg-gray-100 hover:text-gray-900"
                                                type="submit"
                                                x-bind:disabled="buttonDisabled"
                                                dusk="duplicate"
                                            >
                                                <div class="title">
                                                    <i class="fad fa-clone"></i>
                                                    Dupliceren
                                                </div>
                                            </button>
                                        </form>

                                        <div
                                            v-else-if="node.website_page.is_deletable == 1"
                                            class="flex w-full px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                                            title="Deze pagina hoort bij een module die maar één pagina ondersteunt en kan niet gedupliceerd worden."
                                            dusk="duplicate-disabled"
                                        >
                                            <div class="title">
                                                <i class="fad fa-clone"></i>
                                                Dupliceren
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <a
                                    :href="
                                        '/admin/modules/website/website-pages/' +
                                        (node.website_page ? node.website_page.id : '') +
                                        '/edit?menu=' +
                                        filteredWebsiteMenu.id
                                    "
                                    class="mr-3 text-white transition bg-yellow-500 rounded-full hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-yellow-500"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="15.919"
                                        height="15.919"
                                        viewBox="0 0 15.919 15.919"
                                    >
                                        <path
                                            id="Icon_feather-edit-2"
                                            data-name="Icon feather-edit-2"
                                            d="M13.529,3.839a1.985,1.985,0,1,1,2.808,2.808L6.861,16.123,3,17.176l1.053-3.861Z"
                                            transform="translate(-2 -2.257)"
                                            fill="none"
                                            stroke="#fff"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                        />
                                    </svg>
                                </a>

                                <form
                                    v-if="node.website_page.is_deletable"
                                    method="post"
                                    :action="
                                        '/admin/modules/website/website-pages/' +
                                        (node.website_page ? node.website_page.id : '') +
                                        '?menu=' +
                                        filteredWebsiteMenu.id
                                    "
                                >
                                    <input name="_method" type="hidden" value="DELETE"/>
                                    <input type="hidden" name="_token" :value="csrfToken"/>
                                    <button
                                        class="text-white transition bg-red-500 rounded-full hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-red-500"
                                        type="submit"
                                        data-confirm="Weet u zeker dat u dit item wilt verwijderen?"
                                        :dusk="'destroy-' + (node.website_page ? node.website_page.id : '')"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="13.393"
                                            height="13.389"
                                            viewBox="0 0 13.393 13.389"
                                        >
                                            <path
                                                id="Icon_ionic-ios-close"
                                                data-name="Icon ionic-ios-close"
                                                d="M19.568,17.983,24.352,13.2a1.121,1.121,0,0,0-1.585-1.585L17.983,16.4,13.2,11.615A1.121,1.121,0,1,0,11.615,13.2L16.4,17.983l-4.783,4.783A1.121,1.121,0,1,0,13.2,24.352l4.783-4.783,4.783,4.783a1.121,1.121,0,1,0,1.585-1.585Z"
                                                transform="translate(-11.285 -11.289)"
                                                fill="#fff"
                                            />
                                        </svg>
                                    </button>
                                </form>
                            </div>
                        </template>
                    </div>
                </template>
            </Tree>
        </div>
    </div>
</template>

<script>
import {Draggable, Tree} from 'he-tree-vue';
import SparkLineComponent from './SparkLineComponent';

export default {
    components: {
        Tree: Tree.mixPlugins([Draggable]),
        SparkLineComponent,
    },

    props: {
        filteredWebsiteMenu: Object,
        websiteMenuPages: Array,
        maxStatisticValue: Number,
        languages: Array,
        language: String,
        fallbackLanguage: String,
        // Whether the project has opted into the canvas a11y scanner via
        // `galaxy.common.wcag = true`. When false, the per-row red dot
        // is hidden — projects that haven't enabled WCAG mode get a
        // clean tree without the indicator. The cached count column may
        // still exist on `website_pages` (populated by the observer)
        // but visually we stay silent.
        a11yEnabled: { type: Boolean, default: false },
    },

    data() {
        return {
            localWebsiteMenuPages: this.websiteMenuPages,
            draggableEnabled: false,
            csrfToken: null,
            orderInfo: false,
            orderSaved: false,
            renderComponent: true,
            childIndicator: null,
        };
    },

    mounted() {
        // Set csrf token
        this.csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

        // Hide formActions by default
        this.hideFormActions();

        // Prepare buttons for actions
        this.initSaveOrder();
        this.initSaveButton();
        this.initCancelButton();

        this.initTreeBackgroundColors();
    },

    methods: {
        // Set items draggable (and/or save items?)
        initSaveOrder() {
            // Listen for save order button click.
            $('.website\\:\\:admin-websitePages-Index .changeOrder').on('click', (event) => {
                if (!$(event.currentTarget).attr('disabled')) {
                    // Set tree to draggable
                    this.draggableEnabled = true;
                    this.orderInfo = true;

                    // Hide saved message
                    this.orderSaved = false;

                    // Set value and toggle class
                    this.disableChangeOrder();
                    this.showFormActions();
                    this.fixTreeBackgroundColors();
                }
            });
        },

        initSaveButton() {
            $('.website\\:\\:admin-websitePages-Index .saveButton').on('click', (event) => {
                // Disable draggable
                this.draggableEnabled = false;
                this.orderInfo = false;

                // Hide form action buttons
                this.hideFormActions();

                // Re-enable changeOrder button
                this.enableChangeOrder();

                // Save tree data
                this.saveData();
            });
        },

        initCancelButton() {
            $('.website\\:\\:admin-websitePages-Index .cancelButton').on('click', (event) => {
                // Set tree to draggable
                this.draggableEnabled = false;
                this.orderInfo = false;

                // Hide saved message
                this.orderSaved = false;

                // Hide form action buttons
                this.hideFormActions();

                // Reload page to refresh order
                location.reload();
            });
        },

        hideFormActions() {
            $('.website\\:\\:admin-websitePages-Index .formActions').addClass('hidden');
        },

        showFormActions() {
            $('.website\\:\\:admin-websitePages-Index .formActions').removeClass('hidden');
        },

        enableChangeOrder() {
            $('.website\\:\\:admin-websitePages-Index .changeOrder')
                .removeClass('cursor-not-allowed disabled')
                .addClass('cursor-pointer')
                .attr('disabled', false);
        },

        disableChangeOrder() {
            $('.website\\:\\:admin-websitePages-Index .changeOrder')
                .addClass('cursor-not-allowed disabled')
                .removeClass('cursor-pointer')
                .attr('disabled', true);
        },

        saveData() {
            // Add csrf token
            this.localWebsiteMenuPages._token = this.csrfToken;

            // Save data
            $.post('/admin/modules/website/website-pages/saveOrder', {
                website_menu_id: this.filteredWebsiteMenu.id,
                children: JSON.stringify(this.$refs['Tree'].getPureTreeData()),
            }).done(() => {
                // Show saved message
                this.orderSaved = true;

                // Force rerender to prevent still being able to drag items
                this.forceRerender();
            });
        },

        forceRerender() {
            // Remove component from the DOM
            this.renderComponent = false;

            // Execute on next tick
            this.$nextTick(() => {
                // Add the component back in
                this.renderComponent = true;
            });

            this.fixTreeBackgroundColors();
        },

        initTreeBackgroundColors() {
            this.$nextTick(() => {
                let treeEl = $('.tree-branch');
                treeEl.last().addClass('last');

                treeEl
                    .even()
                    .get()
                    .forEach((treeElement) => {
                        this.setTreeBackgroundColor(treeElement, true);
                    });

                treeEl
                    .odd()
                    .get()
                    .forEach((treeElement) => {
                        this.setTreeBackgroundColor(treeElement, false);
                    });
            });
        },

        setTreeBackgroundColor(treeElement, even = true) {
            let style = treeElement.style;

            if (even) {
                style.setProperty('--background-color', 'white');
            } else {
                style.setProperty('--background-color', 'rgb(250 250 250 / var(--tw-bg-opacity))');
            }
        },

        fixTreeBackgroundColors() {
            this.$nextTick(() => {
                this.initTreeBackgroundColors();
            });
        },

        startChildIndicator($event) {
            $('.website\\:\\:admin-websitePages-index-TreeViewComponent').on('mousemove', () => {
                let placeholder = $('.tree-placeholder');
                let nthChild = placeholder.parents('.tree-branch').length;
                let margin = nthChild * 30;

                // placeholder.css('margin-left', margin + 'px');
            });
        },

        stopChildIndicator() {
            $('.website\\:\\:admin-websitePages-index-TreeViewComponent').off('mousemove');
        },

        getNodeLevel(path) {
            return path.length;
        },
    },

    filters: {
        formatDate(date) {
            return moment(date, 'YYYY-MM-DD').format('DD-MM-YYYY');
        },
    },

    computed: {
        Math() {
            return window.Math;
        },

        isMultilanguage() {
            return this.languages.length > 1;
        },

        getLanguage() {
            // Return the fallback language if `language` is empty, else return the current language
            return this.language || this.fallbackLanguage;
        }
    },
};
</script>

<style lang="scss">
.website\:\:admin-websitePages-index-TreeViewComponent {
    // Desktop: scroll horizontally when the columns don't fit, instead of compressing.
    // Mobile uses a stacked card layout, so no horizontal scroll there.
    @screen lg {
        overflow-x: auto;
    }

    // Mobile: drop he-tree's indent (it sets an inline padding-left on .tree-node-back)
    // so cards use the full width — depth is shown by the level number instead.
    @media (max-width: 1023px) {
        .tree-node-back {
            padding-left: 0 !important;
        }
    }

    .dataTable {
        @apply w-full divide-y divide-gray-200 rounded-t-xl overflow-hidden;
        @screen lg { min-width: 64rem; }
        margin-bottom: 0 !important;
        border-bottom-width: 0;
        border-left-width: 1px;
        border-right-width: 1px;
        border-top-width: 1px;
        border-spacing: revert;

        th.language {
            width: 45px;
            padding: 0;
            vertical-align: middle;
            text-align: center;
        }

        th.user {
            padding-left: 30px;
        }

        thead {
            @apply bg-gray-50;

            th {
                @apply px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;

                &:before {
                    @apply mr-1;
                }
            }
        }
    }

    .he-tree {
        @apply border rounded-b-xl;
        @screen lg { min-width: 64rem; }

        .tree-placeholder-node {
            @apply border-4 border-dashed border-gray-200 bg-gray-100 rounded-lg;
            height: 67px;
        }

        .dragging .tree-node-back:hover {
            background-color: inherit;
        }

        .tree-root {
            > .tree-branch {
                > .tree-node-back {
                    > .tree-node {
                        @screen lg {
                            padding-left: 25px !important;
                        }
                    }
                }
            }

            .tree-branch {
                --background-color: white;

                &.last .tree-node-back:after {
                    @apply rounded-b-2xl;
                }

                .tree-node-back {
                    position: relative;

                    &:not(.tree-placeholder):after {
                        content: '';
                        position: absolute;
                        background-color: var(--background-color);
                        top: 0;
                        bottom: -1px;
                        left: 0;
                        right: 0;
                        z-index: -10;
                    }

                    .tree-node {
                        padding: 5px 15px;

                        @screen lg {
                            padding: 5px 5px 5px 0;
                        }

                        > div {
                            @apply bg-gray-50 border gap-x-4 gap-y-3 lg:gap-8 items-start lg:items-center grid grid-cols-2 my-4 px-4 py-3 rounded-2xl lg:bg-transparent lg:m-0 lg:p-0 lg:rounded-none lg:border-0 relative;

                            // Mobile/tablet ONLY (below lg): 2-column card pairing — title + actions
                            // span both columns; Type|Gepubliceerd and Statistieken|Laatst gewijzigd
                            // paired. At lg the DOM order already matches the header, so no order runs.
                            @media (max-width: 1023px) {
                                > .page-title-cell { order: 1; grid-column: span 2; }
                                > .type            { order: 2; }
                                > .publish         { order: 3; }
                                > .statistics      { order: 4; }
                                > .user            { order: 5; }
                                > .languages       { order: 6; grid-column: span 2; }
                                > .edit_actions    { order: 7; grid-column: span 2; }
                            }

                            @screen lg {
                                grid-template-columns: 1fr 80px 110px 150px 120px 150px;

                                &.languages {
                                    grid-template-columns: 1fr 80px 110px auto 150px 120px 150px;
                                }
                            }
                        }

                        .statistics {

                        }

                        .languages {
                            &:before {
                                content: '';
                                position: absolute;
                                top: -8px;
                                bottom: -8px;
                                margin-left: -5px;
                            }

                            .language {
                                width: 45px;
                                display: inline-flex;
                                justify-content: center;
                                align-items: center;

                                i {
                                    color: #55ce63;

                                    &.disabled {
                                        color: #999;
                                    }

                                    &.fa-check.incomplete {
                                        color: orange;
                                    }
                                }
                            }
                        }

                        .user {

                            &:before {
                                content: '';
                                position: absolute;
                                top: -8px;
                                bottom: -8px;
                                margin-left: -25px;
                            }

                            > div {
                                width: 100%;
                            }

                            .profile-img {
                                img {
                                    margin-top: 5px;
                                    max-height: 30px;
                                }

                                .dummy {
                                    width: 30px;
                                    height: 30px;
                                    object-fit: cover;
                                }
                            }

                            .edited {
                                flex: 1 0 auto;

                                .name {
                                    font-size: 13px;
                                    font-weight: bold;
                                    margin: 0 3px 0px 10px;
                                    max-width: 80px;
                                    white-space: nowrap;
                                    text-overflow: ellipsis;
                                    overflow: hidden;
                                    line-height: 1.7em;
                                }

                                .date {
                                    font-size: 12px;
                                    font-weight: 400;
                                    margin: 0 3px 0px 10px;
                                    max-width: 80px;
                                    white-space: nowrap;
                                    line-height: 1.4em;
                                }
                            }
                        }

                        .publish {

                            &:before {
                                content: '';
                                position: absolute;
                                top: -8px;
                                bottom: -8px;
                                margin-left: -25px;
                            }
                        }

                        .edit_actions {
                            @apply pr-3 py-3;

                            &:before {
                                content: '';
                                position: absolute;
                                top: -8px;
                                bottom: -8px;
                                margin-left: -25px;
                            }

                            > a,
                            button:not(.page-dup) {
                                color: white;
                                font-size: 14px;
                                height: 30px;
                                padding: 7px;
                                width: 30px;
                                display: inline-flex;
                                justify-content: center;
                                align-items: center;
                            }
                        }
                    }
                }
            }
        }
    }

    .empty-tree {
        @apply px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 rounded-b-xl bg-white;
        border: 1px solid #e5e7eb;
    }

    &.drag .tree-branch {
        @apply cursor-pointer;
    }
}

.changeOrder {
    &:after {
        content: 'Wijzig volgorde';
        display: inline;
    }
}
</style>
